import React, { useState, useEffect, useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import "@/App.css";

// ===== ASSETS - Actual Engagement Photos =====
const ASSETS = {
  logoWhite: "https://customer-assets.emergentagent.com/job_06157d64-34dc-4108-ba4b-53d358bc39c7/artifacts/hc0apjfz_Logo1_white.png",
  logoBlack: "https://customer-assets.emergentagent.com/job_06157d64-34dc-4108-ba4b-53d358bc39c7/artifacts/ne7yhyqn_logo1_black.png",
  // Engagement photos - Original set
  hero: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/rvtx019v_IMG_7037.jpeg",
  walkingAway: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/wj2bgvlp_IMG_7048.jpeg",
  running: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/8ekor1x4_IMG_7042.jpeg",
  spinning: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/15p5qlw7_IMG_7047.jpeg",
  smiling: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/y889itkr_IMG_7046.jpeg",
  dreamyClose: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/9y5z4chr_IMG_7045.jpeg",
  embrace: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/w43wsj21_IMG_7044.jpeg",
  // Engagement photos - Additional set
  running: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/8ekor1x4_IMG_7042.jpeg",
  holdingHands: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/329z4sma_IMG_7041.jpeg",
  lyingDown: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/oeez7ai4_IMG_7040.jpeg",
  liftHug: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/rvtx019v_IMG_7037.jpeg",
  sitting: "https://customer-assets.emergentagent.com/job_forever-begins-17/artifacts/gac8ud9h_IMG_7033.jpeg",
  // Sedona landscape
  sedona: "https://images.unsplash.com/photo-1616056218484-0b4038f34536?w=1920&q=85"
};

// Wedding date: September 3, 2026
const WEDDING_DATE = new Date("2026-09-03T00:00:00");

// ===== HOOKS =====
const useCountdown = (targetDate) => {
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateCountdown = () => {
      const now = new Date();
      const difference = targetDate - now;

      if (difference > 0) {
        setCountdown({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateCountdown();
    const timer = setInterval(calculateCountdown, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return countdown;
};

// ===== COMPONENTS =====
const LazyImage = ({ src, alt, className, ...props }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} ${loaded ? 'loaded' : ''}`}
      onLoad={() => setLoaded(true)}
      loading="lazy"
      {...props}
    />
  );
};

// Premium fade-in animation wrapper
const FadeIn = ({ children, className = "", delay = 0, direction = "up" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const directions = {
    up: { y: 40, x: 0 },
    down: { y: -40, x: 0 },
    left: { x: 40, y: 0 },
    right: { x: -40, y: 0 },
    none: { x: 0, y: 0 }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ 
        opacity: 0, 
        ...directions[direction]
      }}
      animate={isInView ? { 
        opacity: 1, 
        y: 0, 
        x: 0 
      } : {}}
      transition={{ 
        duration: 0.9, 
        delay, 
        ease: [0.25, 0.4, 0.25, 1]
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Scale reveal animation
const ScaleIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 1.05 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ 
        duration: 1.2, 
        delay, 
        ease: [0.25, 0.4, 0.25, 1]
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ===== HERO SECTION =====
const HeroSection = () => {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section 
      className="hero-section" 
      data-testid="hero-section"
    >
      {/* Background Image using img tag for CORS compatibility */}
      <div className="hero-image-wrapper">
        <img
          src={ASSETS.hero}
          alt="Rach and Dev walking together on mountain overlook"
          className="hero-bg-image"
          data-testid="hero-image"
        />
      </div>
      <div className="hero-overlay" />

      {/* Hero Content */}
      <motion.div className="hero-content" style={{ opacity }}>
        <motion.p
          className="sans-caps text-white/70 mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          A story that began in North Carolina
        </motion.p>
        
        <motion.h1
          className="serif-display text-white text-5xl md:text-7xl lg:text-8xl mb-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          data-testid="hero-title"
        >
          Rach & Dev
        </motion.h1>
        
        <motion.p
          className="sans-caps-lg text-white/80 mb-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          Eloping in Sedona
        </motion.p>
        
        <motion.p
          className="serif-display text-white/90 text-2xl md:text-3xl font-light italic"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          data-testid="hero-date"
        >
          September 3, 2026
        </motion.p>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div 
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
      >
        <span className="sans-caps text-white/50 text-[10px]">Scroll</span>
        <div className="scroll-line" />
      </motion.div>
    </section>
  );
};

// ===== INTRO SECTION =====
const IntroSection = () => {
  return (
    <section className="intro-section" data-testid="intro-section">
      <div className="max-w-3xl mx-auto">
        <FadeIn>
          <div className="elegant-divider mx-auto mb-10" />
        </FadeIn>
        
        <FadeIn delay={0.1}>
          <p className="serif-display text-2xl md:text-3xl lg:text-4xl text-brand-black/80 leading-relaxed mb-8">
            Rachael and Devyn both walked the same campus at UNC Greensboro, yet somehow never crossed paths. 
            The invisible string theory is real, and the universe had other plans for when they would finally meet.
          </p>
        </FadeIn>
        
        <FadeIn delay={0.2}>
          <p className="text-brand-muted text-lg">
            What began as a simple first date in Cary, North Carolina became the start of something beautiful.
          </p>
        </FadeIn>
      </div>
    </section>
  );
};

// ===== OUR STORY SECTION =====
const StorySection = () => {
  return (
    <section className="story-section" data-testid="story-section">
      <div className="story-container">
        <FadeIn className="mb-12 md:mb-16">
          <p className="sans-caps text-brand-muted mb-4">How It All Began</p>
          <h2 className="serif-display text-5xl md:text-6xl lg:text-7xl text-brand-black" data-testid="story-title">
            Our Story
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Images Column */}
          <div className="story-images lg:sticky lg:top-24">
            <ScaleIn className="story-main-image">
              <LazyImage
                src={ASSETS.holdingHands}
                alt="Rachael and Devyn holding hands"
                data-testid="story-image-main"
              />
            </ScaleIn>
            <ScaleIn delay={0.2} className="story-accent-image">
              <LazyImage
                src={ASSETS.walkingAway}
                alt="Rach and Dev walking toward the mountains"
                data-testid="story-image-accent"
              />
            </ScaleIn>
          </div>

          {/* Story Text Column */}
          <div className="space-y-8 lg:pt-8">
            <FadeIn delay={0.1}>
              <p className="text-lg md:text-xl leading-relaxed text-brand-black/85">
                Years later, they matched on Hinge. Their first date happened at Devyn's office building in Cary, North Carolina. What was supposed to be a simple first meeting turned into something neither of them expected.
              </p>
            </FadeIn>
            
            <FadeIn delay={0.3}>
              <p className="text-lg md:text-xl leading-relaxed text-brand-black/85">
                During that first date, they played Uno.
              </p>
            </FadeIn>
            
            <FadeIn delay={0.4}>
              <p className="text-xl md:text-2xl font-medium text-brand-black leading-relaxed">
                Rachael beat Devyn seven times in a row.
              </p>
            </FadeIn>
            
            <FadeIn delay={0.5}>
              <div className="border-l-2 border-brand-gold-soft pl-6 py-2">
                <p className="pull-quote text-2xl md:text-3xl text-brand-black/75">
                  Devyn knew from that first date that he was going to marry her.
                </p>
              </div>
            </FadeIn>
            
            <FadeIn delay={0.6}>
              <p className="text-lg md:text-xl italic text-brand-muted leading-relaxed">
                Rachael left that first date wondering why he didn't kiss her.
              </p>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
};

// ===== PULL QUOTE SECTION =====
const PullQuoteSection = () => {
  return (
    <section className="pullquote-section" data-testid="pullquote-section">
      <div className="max-w-4xl mx-auto">
        <FadeIn>
          <p className="serif-display text-3xl md:text-4xl lg:text-5xl text-brand-black/85 italic leading-snug">
            "Seven games of Uno, one first date, and the quiet certainty that this was the beginning of forever."
          </p>
        </FadeIn>
        <FadeIn delay={0.2}>
          <div className="elegant-divider mx-auto mt-10" />
        </FadeIn>
      </div>
    </section>
  );
};

// ===== SEDONA SECTION =====
const SedonaSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="sedona-section" data-testid="sedona-section">
      <motion.div className="absolute inset-0" style={{ y }}>
        <LazyImage
          src={ASSETS.sedona}
          alt="Sedona Arizona red rocks landscape"
          className="sedona-bg"
          data-testid="sedona-image"
        />
      </motion.div>
      <div className="sedona-overlay" />

      <FadeIn className="sedona-card">
        <p className="sans-caps text-brand-muted mb-4">The Destination</p>
        <h2 className="serif-display text-4xl md:text-5xl text-brand-black mb-8" data-testid="sedona-title">
          Why Sedona
        </h2>
        
        <div className="space-y-5 text-brand-black/80 text-lg leading-relaxed">
          <p>
            In 2023, Rachael and Devyn visited Sedona for the very first time. 
            They were completely captivated by its breathtaking red rock formations, 
            the way the light painted the landscape at golden hour, and the profound 
            sense of peace that settled over them.
          </p>
          <p>
            They knew then that this magical place would hold special meaning for them. 
            When it came time to choose where to begin their marriage, there was no question.
          </p>
          <p className="italic text-brand-muted">
            Sedona called them back.
          </p>
        </div>
        
        <div className="mt-10 pt-8 border-t border-brand-border">
          <p className="sans-caps text-brand-gold tracking-widest">
            Sedona, Arizona
          </p>
        </div>
      </FadeIn>
    </section>
  );
};

// ===== LOVERS KNOLL SECTION =====
const LoversKnollSection = () => {
  const images = [
    "https://images.unsplash.com/photo-1763153818347-555a34a55e5d?w=800&q=85",
    "https://images.unsplash.com/photo-1612805930567-10a48e73295c?w=800&q=85",
    "https://images.unsplash.com/photo-1603928565262-faad75de5e03?w=800&q=85",
  ];

  return (
    <section className="lovers-knoll-section" data-testid="lovers-knoll-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <FadeIn className="text-center mb-12 md:mb-16">
          <p className="sans-caps text-brand-muted mb-4">The Ceremony</p>
          <h2 className="serif-display text-5xl md:text-6xl lg:text-7xl text-brand-black mb-6" data-testid="lovers-knoll-title">
            Baldwin Trail
          </h2>
          <p className="text-lg md:text-xl text-brand-black/70 max-w-2xl mx-auto">
            A sacred spot in the heart of Sedona's red rock country, where Cathedral Rock rises against the endless Arizona sky.
          </p>
        </FadeIn>

        {/* Image Grid */}
        <div className="lovers-knoll-grid mb-12 md:mb-16">
          <ScaleIn className="lovers-knoll-main">
            <LazyImage
              src={images[0]}
              alt="Cathedral Rock at sunset"
              data-testid="lovers-knoll-image-1"
            />
          </ScaleIn>
          <ScaleIn delay={0.1} className="lovers-knoll-side">
            <LazyImage
              src={images[1]}
              alt="Sedona red rock formations"
              data-testid="lovers-knoll-image-2"
            />
          </ScaleIn>
          <ScaleIn delay={0.2} className="lovers-knoll-side">
            <LazyImage
              src={images[2]}
              alt="Cathedral Rock panoramic view"
              data-testid="lovers-knoll-image-3"
            />
          </ScaleIn>
        </div>

        <FadeIn delay={0.2} className="text-center max-w-2xl mx-auto">
          <p className="text-lg md:text-xl text-brand-black/75 leading-relaxed mb-6">
            We'll meet at Baldwin Trailhead on Verde Valley School Road and walk together—about 
            5 minutes—to a ceremony spot with sweeping views of Cathedral Rock and the surrounding red rock formations.
          </p>
          <p className="pull-quote text-2xl md:text-3xl text-brand-black/60 italic">
            This is where our forever begins.
          </p>
        </FadeIn>
      </div>
    </section>
  );
};

// ===== DATE SECTION =====
const DateSection = () => {
  const countdown = useCountdown(WEDDING_DATE);

  return (
    <section className="date-section" data-testid="date-section">
      <FadeIn>
        <p className="sans-caps text-brand-muted mb-6">Mark Your Calendar</p>
      </FadeIn>
      
      <FadeIn delay={0.1}>
        <h2 
          className="serif-display text-6xl md:text-8xl lg:text-9xl text-brand-black mb-4" 
          data-testid="date-heading"
        >
          September 3
        </h2>
      </FadeIn>
      
      <FadeIn delay={0.2}>
        <p className="serif-display text-3xl md:text-4xl text-brand-muted italic">
          Two Thousand Twenty-Six
        </p>
      </FadeIn>
      
      <FadeIn delay={0.3}>
        <p className="sans-caps text-brand-light-muted mt-6">
          Sedona, Arizona
        </p>
      </FadeIn>

      <FadeIn delay={0.4}>
        <div className="countdown-container" data-testid="countdown">
          <div className="countdown-item">
            <span className="countdown-number" data-testid="countdown-days">{countdown.days}</span>
            <span className="countdown-label">Days</span>
          </div>
          <div className="countdown-item">
            <span className="countdown-number" data-testid="countdown-hours">{countdown.hours}</span>
            <span className="countdown-label">Hours</span>
          </div>
          <div className="countdown-item">
            <span className="countdown-number" data-testid="countdown-minutes">{countdown.minutes}</span>
            <span className="countdown-label">Minutes</span>
          </div>
          <div className="countdown-item">
            <span className="countdown-number" data-testid="countdown-seconds">{countdown.seconds}</span>
            <span className="countdown-label">Seconds</span>
          </div>
        </div>
      </FadeIn>
    </section>
  );
};

// ===== CELEBRATION DINNER SECTION =====
const CelebrationSection = () => {
  const venueImages = [
    "https://gallery.streamlinevrs.com/units-gallery/00/0E/A6/image_172561600.jpeg",
    "https://gallery.streamlinevrs.com/units-gallery/00/0E/A6/image_172561636.jpeg",
    "https://gallery.streamlinevrs.com/units-gallery/00/0E/A6/image_172562005.jpeg",
    "https://gallery.streamlinevrs.com/units-gallery/00/0E/A6/image_172561671.jpeg",
    "https://gallery.streamlinevrs.com/units-gallery/00/0E/A6/image_172561598.jpeg",
  ];

  const hotels = [
    {
      name: "Camp Avalon",
      description: "Spiritual nature retreat along Oak Creek with camping, meditation, and red rock views",
      price: "$35 to $50",
      type: "Campground",
      link: "https://campavalon.org/reserve"
    },
    {
      name: "Courtyard by Marriott Sedona",
      description: "Modern hotel with heated pool, hot tub, and red rock views",
      price: "$150 to $300",
      type: "Hotel",
      link: "https://www.marriott.com/en-us/hotels/flgcs-courtyard-sedona/overview/"
    },
    {
      name: "Hilton Vacation Club Sedona Summit",
      description: "Spacious suites with kitchens, pools, and stunning canyon views",
      price: "$180 to $300",
      type: "Resort",
      link: "https://www.hilton.com/en/hotels/sdaborv-hilton-vacation-club-sedona-summit/"
    },
  ];

  return (
    <section className="celebration-section" data-testid="celebration-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Venue Info */}
        <FadeIn className="text-center mb-12 md:mb-16">
          <p className="sans-caps text-brand-muted mb-4">The Evening Before</p>
          <h2 className="serif-display text-5xl md:text-6xl lg:text-7xl text-brand-black mb-6" data-testid="celebration-title">
            Welcome Cocktails
          </h2>
          <p className="text-lg md:text-xl text-brand-black/70 max-w-2xl mx-auto mb-4">
            Everyone is invited to join us for cocktails, hors d'oeuvres, and a relaxed 
            welcome evening before the big day at Pinto Lane Estate.
          </p>
          <p className="text-sm text-brand-muted italic">
            35 Pinto Lane, Sedona, AZ · This is a private rental estate, so house rules and quiet hours will apply.
          </p>
        </FadeIn>

        {/* Venue Images */}
        <div className="airbnb-gallery mb-16">
          <ScaleIn className="airbnb-main">
            <LazyImage
              src={venueImages[0]}
              alt="Pinto Lane Estate exterior with Sedona mountain backdrop"
              data-testid="celebration-image-1"
            />
          </ScaleIn>
          <ScaleIn delay={0.1} className="airbnb-item">
            <LazyImage
              src={venueImages[1]}
              alt="Covered pavilion dining with red rock sunset views"
              data-testid="celebration-image-2"
            />
          </ScaleIn>
          <ScaleIn delay={0.15} className="airbnb-item">
            <LazyImage
              src={venueImages[2]}
              alt="Garden archway leading to the pool"
              data-testid="celebration-image-3"
            />
          </ScaleIn>
          <ScaleIn delay={0.2} className="airbnb-item">
            <LazyImage
              src={venueImages[3]}
              alt="Grand entryway of the estate"
              data-testid="celebration-image-4"            />
          </ScaleIn>
          <ScaleIn delay={0.25} className="airbnb-item">
            <LazyImage
              src={venueImages[4]}
              alt="Aerial view of the estate at dusk"
              data-testid="celebration-image-5"
            />
          </ScaleIn>
        </div>

        <FadeIn delay={0.2} className="text-center mb-16">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full border border-brand-border">
            <span className="sans-caps text-brand-muted text-xs">September 2, 2026</span>
            <span className="w-1 h-1 rounded-full bg-brand-gold" />
            <span className="sans-caps text-brand-muted text-xs">6:30 PM</span>
          </div>
        </FadeIn>

        {/* Hotel Recommendations */}
        <FadeIn className="text-center mb-10">
          <h3 className="serif-display text-3xl md:text-4xl text-brand-black mb-4">
            Where to Stay
          </h3>
          <p className="text-brand-black/60 max-w-xl mx-auto mb-3">
            These options are all a short drive from the celebration venue. 
            Prices are estimates for September 2026.
          </p>
          <p className="text-brand-black/60 max-w-xl mx-auto">
            You can also browse Airbnb for unique vacation rentals in the area.
          </p>
        </FadeIn>

        <div className="hotel-grid">
          {hotels.map((hotel, index) => (
            <FadeIn key={hotel.name} delay={index * 0.1}>
              <div className="hotel-card">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-medium text-brand-black text-lg">{hotel.name}</h4>
                  <span className="sans-caps text-[10px] text-brand-muted">{hotel.type}</span>
                </div>
                <p className="text-sm text-brand-black/60 mb-4">{hotel.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-brand-black font-medium">{hotel.price}<span className="text-sm text-brand-muted font-normal">/night</span></span>
                  <a
                    href={hotel.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hotel-book-btn"
                    data-testid={`hotel-book-${index}`}
                  >
                    Book
                  </a>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===== ITINERARY SECTION =====
const ItinerarySection = () => {
  return (
    <section className="itinerary-section" data-testid="itinerary-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <FadeIn className="text-center mb-12">
          <p className="sans-caps text-brand-muted mb-4">The Weekend</p>
          <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl text-brand-black mb-6" data-testid="itinerary-title">
            Itinerary
          </h2>
          <p className="text-brand-black/60">
            More details to come, but here's what to expect.
          </p>
        </FadeIn>

        <div className="itinerary-timeline">
          {/* September 2nd */}
          <FadeIn delay={0.1}>
            <div className="itinerary-day">
              <div className="itinerary-date">
                <span className="serif-display text-2xl md:text-3xl text-brand-black">Sept 2</span>
                <span className="sans-caps text-xs text-brand-muted">Wednesday</span>
              </div>
              <div className="itinerary-content">
                <div>
                  <p className="sans-caps text-xs text-brand-gold mb-1">6:30 PM</p>
                  <h3 className="font-medium text-brand-black text-lg mb-2">Welcome Cocktails</h3>
                  <p className="text-brand-black/70 mb-2">
                    Pinto Lane Estate · 35 Pinto Lane, Sedona, AZ
                  </p>
                  <p className="text-brand-black/70">
                    Everyone is invited to join us for cocktails, hors d'oeuvres, and a relaxed welcome evening before the big day.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* September 3rd */}
          <FadeIn delay={0.2}>
            <div className="itinerary-day">
              <div className="itinerary-date">
                <span className="serif-display text-2xl md:text-3xl text-brand-black">Sept 3</span>
                <span className="sans-caps text-xs text-brand-muted">Thursday</span>
              </div>
              <div className="itinerary-content">
                <div className="space-y-6">
                  <div>
                    <p className="sans-caps text-xs text-brand-gold mb-1">3:45 PM</p>
                    <h3 className="font-medium text-brand-black text-lg mb-2">Meet at Baldwin Trailhead</h3>
                    <p className="text-brand-black/70 mb-2">
                      Verde Valley School Road, Sedona, AZ 86351
                    </p>
                    <p className="text-brand-black/70">
                      Please navigate specifically to "Baldwin Trailhead." There is parking at the trailhead. 
                      We'll meet there and walk together—it's about a 5-minute walk to the ceremony spot.
                    </p>
                  </div>
                  <div>
                    <p className="sans-caps text-xs text-brand-gold mb-1">4:00 PM</p>
                    <h3 className="font-medium text-brand-black text-lg mb-2">Ceremony + Personal Vows</h3>
                    <p className="text-brand-black/70">
                      Here we'll exchange our personal vows with everyone gathered around us. 
                      Family photos will follow the ceremony. Family members: please plan to stay nearby for photos.
                    </p>
                  </div>
                  <div>
                    <p className="sans-caps text-xs text-brand-gold mb-1">5:00 PM</p>
                    <h3 className="font-medium text-brand-black text-lg mb-2">Rachael & Devyn Head Off</h3>
                    <p className="text-brand-black/70">
                      We'll leave for our Broken Arrow adventure + photo session.
                    </p>
                  </div>
                  <div>
                    <p className="sans-caps text-xs text-brand-gold mb-1">5:00 – 7:00 PM</p>
                    <h3 className="font-medium text-brand-black text-lg mb-2">While We're Adventuring...</h3>
                    <p className="text-brand-black/70">
                      Feel free to return to your Airbnb to relax and freshen up, or head to Pinto Lane Estate 
                      with the girls to hang out, grab a drink, and help decorate for dinner.
                    </p>
                  </div>
                  <div>
                    <p className="sans-caps text-xs text-brand-gold mb-1">~7:00 PM</p>
                    <h3 className="font-medium text-brand-black text-lg mb-2">Dinner + Celebration</h3>
                    <p className="text-brand-black/70 mb-2">
                      Pinto Lane Estate · 35 Pinto Lane, Sedona, AZ
                    </p>
                    <p className="text-brand-black/70">
                      Dinner, drinks, toasts & celebrating when we return!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.3} className="text-center mt-12">
          <div className="dress-code-card">
            <h3 className="font-medium text-brand-black text-lg mb-3">Attire</h3>
            <p className="text-brand-black/70 mb-4">
              Nice casual—nice shorts and button-downs are welcome.
            </p>
            <p className="text-brand-black/60 text-sm">
              If you'd like to complement the landscape, think desert colors: oranges, reds, browns, and earthy tones.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.4} className="text-center mt-8">
          <p className="text-brand-muted italic">
            Stay tuned for more details as we get closer to the date.
          </p>
        </FadeIn>
      </div>
    </section>
  );
};

// ===== LOGISTICS SECTION =====
const LogisticsSection = () => {
  return (
    <section className="logistics-section" data-testid="logistics-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <FadeIn>
          <p className="sans-caps text-brand-muted mb-4">Getting There</p>
          <h2 className="serif-display text-4xl md:text-5xl text-brand-black mb-8" data-testid="logistics-title">
            Travel Logistics
          </h2>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="logistics-card">
            <div className="logistics-icon mb-6">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
              </svg>
            </div>
            <h3 className="serif-display text-2xl md:text-3xl text-brand-black mb-4">
              Fly into Phoenix
            </h3>
            <p className="text-lg text-brand-black/70 mb-6 leading-relaxed">
              The easiest way to get to Sedona is to fly into <strong>Phoenix Sky Harbor International Airport (PHX)</strong> and rent a car. 
              It's a beautiful two hour drive north through the Arizona desert and up into red rock country.
            </p>
            <div className="bg-brand-warm rounded-2xl p-6 mb-6">
              <div className="mb-5">
                <p className="sans-caps text-xs text-brand-muted mb-2">Ceremony Meeting Point</p>
                <p className="text-brand-black font-medium">Baldwin Trailhead</p>
                <p className="text-brand-black/70">Verde Valley School Road, Sedona, AZ 86351</p>
              </div>
              <div>
                <p className="sans-caps text-xs text-brand-muted mb-2">Cocktails & Celebration Venue</p>
                <p className="text-brand-black font-medium">Pinto Lane Estate</p>
                <p className="text-brand-black/70">35 Pinto Lane, Sedona, AZ</p>
              </div>
            </div>
            <p className="text-brand-muted text-sm">
              We recommend arriving a day early to settle in and enjoy the stunning scenery.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

// ===== GALLERY SECTION =====
const GallerySection = () => {
  const galleryImages = [
    { src: ASSETS.spinning, alt: "Rachael spinning with joy" },
    { src: ASSETS.liftHug, alt: "Devyn lifting Rachael in embrace" },
    { src: ASSETS.holdingHands, alt: "Holding hands with engagement ring" },
    { src: ASSETS.running, alt: "Running through the hills together" },
    { src: ASSETS.sitting, alt: "Sitting together on blanket" },
    { src: ASSETS.lyingDown, alt: "Rachael lying on Devyn" },
  ];

  return (
    <section className="gallery-section" data-testid="gallery-section">
      <div className="max-w-1400 mx-auto">
        <FadeIn className="text-center mb-12 md:mb-16">
          <p className="sans-caps text-brand-muted mb-4">Moments Together</p>
          <h2 className="serif-display text-5xl md:text-6xl text-brand-black" data-testid="gallery-title">
            Gallery
          </h2>
        </FadeIn>

        <div className="gallery-grid">
          {galleryImages.map((img, index) => (
            <ScaleIn 
              key={index}
              className="gallery-item"
              delay={index * 0.15}
            >
              <LazyImage
                src={img.src}
                alt={img.alt}
                data-testid={`gallery-image-${index}`}
              />
            </ScaleIn>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===== CLOSING SECTION =====
const ClosingSection = () => {
  return (
    <section className="closing-section" data-testid="closing-section">
      <FadeIn>
        <div className="closing-image">
          <LazyImage
            src={ASSETS.embrace}
            alt="Rach and Dev embracing"
            data-testid="closing-image"
          />
        </div>
      </FadeIn>
      
      <FadeIn delay={0.2}>
        <p className="serif-display text-3xl md:text-4xl lg:text-5xl italic text-brand-muted mb-8" data-testid="closing-quote">
          "The beginning of forever."
        </p>
      </FadeIn>
      
      <FadeIn delay={0.3}>
        <h2 className="serif-display text-5xl md:text-6xl lg:text-7xl text-brand-black mb-6">
          Rach & Dev
        </h2>
      </FadeIn>
      
      <FadeIn delay={0.4}>
        <p className="sans-caps text-brand-muted">
          September 3, 2026 · Sedona, Arizona
        </p>
      </FadeIn>
      
      <FadeIn delay={0.5}>
        <div className="elegant-divider mx-auto mt-12" />
      </FadeIn>
    </section>
  );
};

// ===== FOOTER =====
const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <FadeIn>
        <div className="footer-logo-text mb-6" data-testid="footer-logo">
          <span className="serif-display text-3xl text-brand-black/70">rach & dev.</span>
        </div>
        <p className="sans-caps text-brand-light-muted text-[10px]">
          Forever Begins Here
        </p>
      </FadeIn>
    </footer>
  );
};

// ===== MAIN APP =====
function App() {
  return (
    <div className="App bg-brand-cream" data-testid="app-container">
      <HeroSection />
      <IntroSection />
      <StorySection />
      <PullQuoteSection />
      <SedonaSection />
      <LoversKnollSection />
      <DateSection />
      <CelebrationSection />
      <ItinerarySection />
      <LogisticsSection />
      <GallerySection />
      <ClosingSection />
      <Footer />
    </div>
  );
}

export default App;
